import React, { useEffect, useMemo, useState } from "react";
import axios from "../utils/api";
import Swal from "sweetalert2";
import logo from "../assets/shoe.png";
import bgImage from "../assets/airmax.webp";

const fmtKesh = (n) =>
  `Kesh${Number(n || 0).toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const todayISO = () => {
  const d = new Date();
  const pad = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const AddressBlock = ({ shopId }) => {
  const id = Number(shopId);
  if (id === 1) {
    return (
      <>
        <p><strong>NAIROBI, KENYA</strong></p>
        <p>Kamukunji Market</p>
        <p>Building: Marpet Plaza, First Floor, Shop No. 20</p>
        <p>Contact: +2540740357257</p>
      </>
    );
  }
  if (id === 2) {
    return (
      <>
        <p><strong>NAIROBI, KENYA</strong></p>
        <p>Kamukunji Market</p>
        <p>Building: Marpet Plaza, Second Floor, Shop No. 46</p>
        <p>Contact: +2540740357257</p>
      </>
    );
  }
  return <p className="text-red-500">⚠️ location info unavailable</p>;
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ id: null, name: "", size: "", color: "", price: "" });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // Header meta (from stock-tracking endpoint like your report)
  const [shopMeta, setShopMeta] = useState({ shop_id: null, shop_name: "" });

  // ---- Load shop header exactly like StockTracking ----
  const loadShopMeta = async () => {
    try {
      const res = await axios.get("/stock-tracking.php", { params: { date: todayISO() } });
      if (res.data?.shop_id) {
        setShopMeta({
          shop_id: res.data.shop_id,
          shop_name: res.data.shop_name || `PETRAH COLLECTION - SHOP #${res.data.shop_id}`,
        });
      } else {
        setShopMeta({ shop_id: null, shop_name: "PETRAH COLLECTION - SHOP #?" });
      }
    } catch {
      setShopMeta({ shop_id: null, shop_name: "PETRAH COLLECTION - SHOP #?" });
    }
  };

  // ---- CRUD: products ----
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/products.php");
      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch {
      Swal.fire("Error", "Failed to fetch products", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (form.id) {
        await axios.put("/products.php", form);
        Swal.fire("✅ Product updated!", "", "success");
      } else {
        await axios.post("/products.php", form);
        Swal.fire("✅ Product added!", "", "success");
      }
      setForm({ id: null, name: "", size: "", color: "", price: "" });
      fetchProducts();
    } catch {
      Swal.fire("❌ Failed", "Check inputs", "error");
    }
  };

  const handleEdit = (p) => {
    setForm({ id: p.id, name: p.name, size: p.size, color: p.color, price: p.price });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      `${p.name} ${p.size} ${p.color} ${p.price}`.toLowerCase().includes(q)
    );
  }, [products, search]);

  const handlePrint = () => window.print();

  useEffect(() => {
    loadShopMeta();
    fetchProducts();
  }, []);

  return (
    <div className="w-full h-full relative overflow-y-auto">
      {/* Background (hidden on print) */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30 blur-sm print:hidden"
        style={{ backgroundImage: `url(${bgImage})` }}
      />
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          .no-print { display: none !important; }
          .card { box-shadow: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="relative z-10 p-6">
        {/* ===== Header (same pattern as stock tracking) ===== */}
        <div className="bg-white rounded-xl shadow card p-6 max-w-5xl mx-auto mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <img src={logo} alt="logo" className="h-12 w-12" />
              <div>
                <h1 className="text-lg font-bold uppercase">
                  {shopMeta.shop_name || "PETRAH COLLECTION"}
                </h1>
                <p className="text-sm text-gray-500">shop id: {shopMeta.shop_id ?? "—"}</p>
              </div>
            </div>
            <div className="text-right text-sm">
              <AddressBlock shopId={shopMeta.shop_id} />
            </div>
          </div>

          <div className="text-center mt-4">
            <h2 className="text-xl font-semibold">All Products</h2>
            <p className="text-xs text-gray-500">
              generated on {new Date().toISOString().replace("T", " ").slice(0, 19)}
            </p>
          </div>

          <div className="mt-4 flex justify-end gap-2 no-print">
            <button
              onClick={handlePrint}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
            >
              🖨️ Print / Save PDF
            </button>
          </div>
        </div>

        {/* ===== Form (hidden on print) ===== */}
        <div className="bg-white/90 backdrop-blur-md rounded-xl shadow card p-6 max-w-5xl mx-auto mb-8 no-print">
          <h2 className="text-2xl font-semibold text-gray-700 mb-6 flex items-center gap-2">
            🛒 {form.id ? "Edit Product" : "Add New Product"}
          </h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="hidden" name="id" value={form.id || ""} />
            <input
              type="text"
              name="name"
              placeholder="Product name"
              value={form.name}
              onChange={handleChange}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
              required
            />
            <input
              type="text"
              name="size"
              placeholder="Size (e.g. 42)"
              value={form.size}
              onChange={handleChange}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
            />
            <input
              type="text"
              name="color"
              placeholder="Color"
              value={form.color}
              onChange={handleChange}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
            />
            <input
              type="number"
              name="price"
              placeholder="Price"
              value={form.price}
              onChange={handleChange}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
              required
            />
            <div className="col-span-1 md:col-span-2">
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-md transition"
              >
                {form.id ? "✏️ Update Product" : "➕ Add Product"}
              </button>
            </div>
          </form>
        </div>

        {/* ===== Table (printable; action hidden) ===== */}
        <div className="bg-white/90 backdrop-blur-md rounded-xl shadow card p-6 max-w-5xl mx-auto">
          <input
            type="text"
            placeholder="🔍 Search by name, color, size..."
            className="w-full mb-4 px-4 py-2 border border-gray-300 rounded-md no-print"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left border border-gray-200">
              <thead className="bg-gray-100 text-gray-700 font-medium">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Size</th>
                  <th className="px-4 py-3">Color</th>
                  <th className="px-4 py-3">Price</th>
                  <th className="px-4 py-3 no-print">Action</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center px-4 py-6 text-gray-500">
                      loading...
                    </td>
                  </tr>
                ) : filtered.length > 0 ? (
                  filtered.map((p) => (
                    <tr key={p.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2">{p.name}</td>
                      <td className="px-4 py-2">{p.size}</td>
                      <td className="px-4 py-2">{p.color}</td>
                      <td className="px-4 py-2">{fmtKesh(p.price)}</td>
                      <td className="px-4 py-2 no-print">
                        <button
                          onClick={() => handleEdit(p)}
                          className="text-blue-600 hover:underline"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center px-4 py-6 text-gray-500">
                      No products found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filtered.length > 0 && (
            <div className="mt-4 flex justify-end text-sm">
              <div className="text-right">
                <p>
                  <span className="font-semibold">Total products:</span> {filtered.length}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Products;
